/* OZZY+ motion engine — cinematic storefront motion.
   Everything is progressive enhancement: without JS the page renders fully. */
(() => {
  "use strict";

  const docEl = document.documentElement;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const finePointer = window.matchMedia("(pointer: fine)").matches;
  const lerp = (a, b, t) => a + (b - a) * t;

  /* ---------------------------------------------------------------- curtain */
  const seenKey = "ozzy-intro-seen";
  const store = {
    get(k) { try { return sessionStorage.getItem(k); } catch { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, v); } catch { /* private mode */ } },
  };
  const buildCurtain = () => {
    if (reduced || store.get(seenKey)) return;
    const el = document.createElement("div");
    el.className = "curtain";
    el.setAttribute("aria-hidden", "true");
    el.innerHTML =
      '<div class="curtain-mark">OZZY<span>+</span></div><div class="curtain-line"></div>';
    document.body.appendChild(el);
    docEl.classList.add("intro-lock");
    store.set(seenKey, "1");
    const finish = () => {
      el.classList.add("curtain--away");
      docEl.classList.remove("intro-lock");
      docEl.classList.add("intro-done");
      document.dispatchEvent(new CustomEvent("ozzy:intro-done"));
      setTimeout(() => el.remove(), 1100);
    };
    requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add("curtain--on")));
    setTimeout(finish, 1650);
  };

  /* --------------------------------------------------- split kinetic letters */
  const splitKinetic = () => {
    document.querySelectorAll("[data-kinetic]").forEach((el) => {
      if (el.dataset.kineticDone) return;
      el.dataset.kineticDone = "1";
      const walk = (node) => {
        if (node.nodeType === 3) {
          const frag = document.createDocumentFragment();
          node.textContent.split(/(\s+)/).forEach((chunk) => {
            if (!chunk) return;
            if (/^\s+$/.test(chunk)) {
              frag.appendChild(document.createTextNode(" "));
              return;
            }
            const word = document.createElement("span");
            word.className = "k-word";
            [...chunk].forEach((ch) => {
              const c = document.createElement("span");
              c.className = "k-char";
              c.textContent = ch;
              word.appendChild(c);
            });
            frag.appendChild(word);
          });
          node.replaceWith(frag);
        } else if (node.nodeType === 1) {
          [...node.childNodes].forEach(walk);
        }
      };
      [...el.childNodes].forEach(walk);
      el.querySelectorAll(".k-word").forEach((word, wi) => {
        word.querySelectorAll(".k-char").forEach((c, ci) => {
          c.style.setProperty("--kd", `${(wi * 2 + ci) * 38}ms`);
        });
      });
      el.classList.add("k-ready");
    });
  };

  /* ----------------------------------------------------------- reveal system */
  let revealObserver = null;
  const armReveals = () => {
    const targets = document.querySelectorAll("[data-reveal], [data-kinetic]");
    if (!targets.length) return;
    if (reduced || !("IntersectionObserver" in window)) {
      targets.forEach((t) => t.classList.add("is-in"));
      return;
    }
    if (revealObserver) {
      targets.forEach((t) => revealObserver.observe(t));
      return;
    }
    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          el.style.setProperty("--rd", `${Number(el.dataset.revealDelay || 0)}ms`);
          el.classList.add("is-in");
          revealObserver.unobserve(el);
          setTimeout(() => el.removeAttribute("data-reveal"), 1700);
        });
      },
      { threshold: 0.14, rootMargin: "0px 0px -8% 0px" }
    );
    targets.forEach((t) => revealObserver.observe(t));
  };

  /* ---------------------------------------------------------- scroll engine */
  const scrollState = { y: 0, vel: 0, raf: 0, ticking: false };

  const initProgressAndHeader = () => {
    const bar = document.querySelector("[data-progress]");
    const header = document.querySelector(".site-header");
    const update = () => {
      const max = docEl.scrollHeight - window.innerHeight;
      const p = max > 0 ? Math.min(1, window.scrollY / max) : 0;
      if (bar) bar.style.transform = `scaleX(${p})`;
      if (header) header.classList.toggle("is-scrolled", window.scrollY > 30);
    };
    window.addEventListener("scroll", update, { passive: true });
    update();
  };

  /* ------------------------------------------------------------ parallax/tilt */
  const initParallax = () => {
    if (reduced) return;
    const layers = [...document.querySelectorAll("[data-parallax]")].map((el) => ({
      el,
      strength: parseFloat(el.dataset.parallax) || 0.18,
      y: 0,
    }));
    const ticking = () => {
      layers.forEach((l) => {
        const r = l.el.getBoundingClientRect();
        const progress = (r.top + r.height / 2 - window.innerHeight / 2) / window.innerHeight;
        const target = -progress * l.strength * 100;
        l.y = lerp(l.y, target, 0.12);
        l.el.style.transform = `translate3d(0, ${l.y.toFixed(2)}px, 0)`;
      });
      scrollState.raf = requestAnimationFrame(ticking);
    };
    if (layers.length) scrollState.raf = requestAnimationFrame(ticking);
  };

  const initTilt = () => {
    if (reduced || !finePointer) return;
    document.querySelectorAll("[data-tilt]").forEach((el) => {
      const glare = el.querySelector("[data-tilt-glare]");
      const max = parseFloat(el.dataset.tilt) || 7;
      let rect = null;
      const move = (e) => {
        rect = rect || el.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        el.style.setProperty("--tx", `${(x * max).toFixed(2)}deg`);
        el.style.setProperty("--ty", `${(-y * max).toFixed(2)}deg`);
        if (glare) {
          glare.style.setProperty("--gx", `${((x + 0.5) * 100).toFixed(1)}%`);
          glare.style.setProperty("--gy", `${((y + 0.5) * 100).toFixed(1)}%`);
          glare.style.opacity = "1";
        }
      };
      const leave = () => {
        rect = null;
        el.style.setProperty("--tx", "0deg");
        el.style.setProperty("--ty", "0deg");
        if (glare) glare.style.opacity = "0";
      };
      el.addEventListener("pointermove", move);
      el.addEventListener("pointerleave", leave);
    });
  };

  /* ------------------------------------------------------------ spotlights */
  const initSpotlight = () => {
    if (!finePointer) return;
    document.querySelectorAll("[data-spotlight]").forEach((el) => {
      el.addEventListener("pointermove", (e) => {
        const r = el.getBoundingClientRect();
        el.style.setProperty("--sx", `${e.clientX - r.left}px`);
        el.style.setProperty("--sy", `${e.clientY - r.top}px`);
        el.classList.add("spot-on");
      });
      el.addEventListener("pointerleave", () => el.classList.remove("spot-on"));
    });
  };

  /* ------------------------------------------------------------- magnetic */
  const initMagnets = () => {
    if (reduced || !finePointer) return;
    document.querySelectorAll("[data-magnet]").forEach((el) => {
      const strength = 22;
      const move = (e) => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left - r.width / 2) / (r.width / 2);
        const y = (e.clientY - r.top - r.height / 2) / (r.height / 2);
        el.style.transform = `translate(${(x * strength).toFixed(1)}px, ${(y * strength * 0.6).toFixed(1)}px)`;
      };
      const leave = () => (el.style.transform = "");
      el.addEventListener("pointermove", move);
      el.addEventListener("pointerleave", leave);
    });
  };

  /* ---------------------------------------------------------- manifesto scrub */
  const initScrub = () => {
    const scrub = document.querySelector("[data-scrub-words]");
    const mark = document.querySelector("[data-watermark]");
    if (!scrub && !mark) return;
    if (reduced) {
      scrub && scrub.classList.add("is-scrubbed");
      return;
    }
    const words = scrub
      ? scrub.textContent.trim().split(/\s+/).map((w) => `<span class="scrub-w">${w}</span>`)
      : [];
    if (scrub && words.length) scrub.innerHTML = words.join(" ");
    const spans = scrub ? [...scrub.querySelectorAll(".scrub-w")] : [];
    let rafId = 0;
    const tick = () => {
      if (scrub) {
        const r = scrub.getBoundingClientRect();
        const start = window.innerHeight * 0.86;
        const end = window.innerHeight * 0.32;
        const raw = (start - r.top) / (start - end);
        const p = Math.max(0, Math.min(1.15, raw));
        const lit = p * spans.length;
        spans.forEach((s, i) => s.classList.toggle("lit", i < lit));
        scrub.classList.toggle("is-scrubbed", p > 0.02);
      }
      if (mark) {
        const sec = mark.closest("section") || mark.parentElement;
        const r = sec.getBoundingClientRect();
        const p = Math.max(0, Math.min(1, 1 - (r.bottom / (window.innerHeight + r.height))));
        mark.style.setProperty("--wms", (0.82 + p * 0.5).toFixed(3));
        mark.style.setProperty("--wmr", `${(-14 + p * 26).toFixed(1)}deg`);
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
  };

  /* ------------------------------------------------------------- marquee kick */
  const initMarquee = () => {
    if (reduced) return;
    const tracks = [...document.querySelectorAll("[data-marquee-reactive] .marquee-track")];
    if (!tracks.length) return;
    let offset = 0;
    let target = 0;
    let lastY = window.scrollY;
    const tick = () => {
      const y = window.scrollY;
      target = Math.max(-60, Math.min(60, (y - lastY) * 0.45));
      lastY = y;
      offset = lerp(offset, target, 0.06);
      tracks.forEach((t) => (t.style.translate = `${offset.toFixed(2)}px 0`));
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  /* ---------------------------------------------------------------- counters */
  const initCounters = () => {
    const els = document.querySelectorAll("[data-count]");
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          io.unobserve(el);
          const end = parseFloat(el.dataset.count) || 0;
          if (reduced) {
            el.textContent = el.dataset.countSuffix ? end + el.dataset.countSuffix : end;
            return;
          }
          const t0 = performance.now();
          const dur = 1400;
          const step = (now) => {
            const p = Math.min(1, (now - t0) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            const val = Math.round(end * eased);
            el.textContent = el.dataset.countSuffix ? val + el.dataset.countSuffix : val;
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        });
      },
      { threshold: 0.6 }
    );
    els.forEach((el) => io.observe(el));
  };

  /* -------------------------------------------------------------- gold dust */
  const initDust = () => {
    if (reduced || !finePointer) return;
    document.querySelectorAll("canvas[data-dust]").forEach((canvas) => {
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const host = canvas.parentElement;
      let w = 0, h = 0, dpr = Math.min(2, window.devicePixelRatio || 1);
      const motes = [];
      const N = 46;
      const resize = () => {
        const r = host.getBoundingClientRect();
        w = Math.max(1, r.width);
        h = Math.max(1, r.height);
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        canvas.style.width = w + "px";
        canvas.style.height = h + "px";
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      };
      resize();
      window.addEventListener("resize", resize);
      for (let i = 0; i < N; i++) {
        motes.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: Math.random() * 1.7 + 0.5,
          sp: Math.random() * 0.28 + 0.08,
          drift: Math.random() * 0.5 - 0.25,
          tw: Math.random() * Math.PI * 2,
          tws: Math.random() * 0.03 + 0.008,
        });
      }
      let running = true;
      document.addEventListener("visibilitychange", () => {
        running = !document.hidden;
        if (running) requestAnimationFrame(tick);
      });
      const tick = () => {
        if (!running) return;
        ctx.clearRect(0, 0, w, h);
        for (const m of motes) {
          m.y -= m.sp;
          m.x += m.drift * 0.3;
          m.tw += m.tws;
          if (m.y < -8) {
            m.y = h + 8;
            m.x = Math.random() * w;
          }
          if (m.x < -8) m.x = w + 8;
          if (m.x > w + 8) m.x = -8;
          const a = 0.16 + Math.sin(m.tw) * 0.14;
          if (a <= 0) continue;
          ctx.beginPath();
          ctx.arc(m.x, m.y, m.r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(217, 167, 46, ${a.toFixed(3)})`;
          ctx.shadowColor = "rgba(217, 167, 46, .8)";
          ctx.shadowBlur = 8;
          ctx.fill();
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  };

  /* ------------------------------------------------------------- gold cursor */
  const initCursor = () => {
    if (reduced || !finePointer) return;
    const dot = document.createElement("div");
    dot.className = "cursor-dot";
    const ring = document.createElement("div");
    ring.className = "cursor-ring";
    document.body.append(dot, ring);
    let x = -100, y = -100, rx = -100, ry = -100, scale = 1;
    window.addEventListener("pointermove", (e) => {
      x = e.clientX;
      y = e.clientY;
      dot.style.opacity = ring.style.opacity = "1";
      const hot = e.target.closest("a, button, [data-tilt], input, select, summary");
      scale = hot ? 1.9 : 1;
    }, { passive: true });
    const tick = () => {
      rx = lerp(rx, x, 0.16);
      ry = lerp(ry, y, 0.16);
      const s = lerp(parseFloat(ring.dataset.s || "1"), scale, 0.14);
      ring.dataset.s = s;
      dot.style.transform = `translate(${x}px, ${y}px)`;
      ring.style.transform = `translate(${rx}px, ${ry}px) scale(${s.toFixed(3)})`;
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  /* ------------------------------------------------------------------- boot */
  const boot = () => {
    buildCurtain();
    docEl.classList.add("motion-ready");
    splitKinetic();
    if (reduced || !document.querySelector(".curtain")) {
      armReveals();
    } else {
      document.addEventListener("ozzy:intro-done", armReveals, { once: true });
    }
    initProgressAndHeader();
    initParallax();
    initTilt();
    initSpotlight();
    initMagnets();
    initScrub();
    initMarquee();
    initCounters();
    initDust();
    initCursor();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})();
